import { useCallback } from 'react'
import { SetAppRoute, toPathFrom } from '~/features/routes'
import { useProcess, useSelect } from '~/store'
import { authModule, currentUser } from './auth/auth-module'
import { routeModule, selRoute } from './route-module'
import { stOrganizationModule } from './st-organizations/st-organization-module'

export function useSetPath(): (path: string) => void {
  const { send } = useProcess('route')
  return useCallback((path: string) => send({ type: 'navigate', pathname: path }), [send])
}

export function useRoute() {
  return useSelect('route', selRoute)
}

export function useSetRoute() {
  const { send } = useProcess('route')
  const currentRoute = useSelect('route', (s) => s.route)
  return useCallback(
    (action: SetAppRoute) => {
      if (!currentRoute) {
        throw new Error('Expected current route to be set before calling useSetRoute')
      }
      const path = toPathFrom(currentRoute, action)
      send({ type: 'navigate', pathname: path })
    },
    [send, currentRoute]
  )
}

export function useAuthState() {
  return useSelect('auth', (s) => s)
}

export function useInitialAuth() {
  return useSelect('auth', (s) => s.initialAuth)
}

export function useCurrentUser() {
  return useSelect('auth', currentUser)
}

/**
 * In most cases, emails are managed by StanfordTax. This means StanfordTax is responsible for sending emails to clients.
 * But with Karbon connected accounts, emails will be sent by Karbon.
 *
 * To avoid confusion, we adapt the interface according to who the emails are managed by.
 * For example, we may block certain email actions for Karbon connected accounts to avoid emails coming from two different sources.
 */
export function useEmailsManagedBy(): 'stanfordtax' | 'karbon' {
  return useSelect('stOrganization', (s) => {
    if (!s || s.status != 'loaded') {
      return 'stanfordtax'
    }
    return s.organizationState.connectedApps.karbon?.emailsManagedBy ?? 'stanfordtax'
  })
}

export function assetURL(filepath: string) {
  return `https://assets.stanfordtax.com/${filepath}`
}
